import {
    Button,
    cleanObject,
    dispatchCustomEvent,
    FDate,
    generateUEID,
    IconLib,
    MenuProvider,
    Portal,
    Text,
    useAlert,
    useCustomEvent,
    useDialog,
    useDrag,
    useStorage,
    useToast,
    View,
    waitForRender,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import {
    CommonProvider,
    dispatchTodoEvent,
    FloatingToolbar,
    FOLD_TODO_DRAG_TASK_CACHE,
    getCollapsedStateFromLocalStorage,
    LoadingLine,
    RichInputOption,
    RichInputProvider,
    TASK_COLLAPSED,
    Todo,
    TodoProvider,
} from '..'
import { Busy } from '../../App'
import { COLORS, MAX_TASK_PASTE, POSITION_INCREMENT } from '../../constants'
import {
    byName,
    byPosition,
    byText,
    getNewPositionForTargetIndex,
    getNewPositionsForTasks,
    getPositionIncrement,
    getRandomColor,
    logger,
    removeExistingElements,
    searchLabels,
    setFastCache,
} from '../../helpers'
import { getNewCompletedTaskDate } from '../../helpers/dates'
import {
    useLabels,
    useProjects,
    useProjectSections,
    useProjectUsers,
    useTaskLabels,
    useTasks,
    useTaskUsers,
    useTeamUsers,
    useWorkspaceUsers,
} from '../../hooks/api.hooks'
import { TaskMenu } from '../../menus/task.menu'
import { useAppStore } from '../../store/app'
import { Calendar, getInitialCalendarView } from '../calendar'
import { processHTML } from '../rich-input/rich-input.util'
import {
    createDatesForUpcoming,
    extractIds,
    findFirstLowerIndentTask,
    findSubtaskCount,
    findTask,
    findTaskById,
    flattenArray,
    flattenTasks,
    getDates,
    getDaysBetween,
    getSavedFilters,
    getSectionIndex,
    getUpcomingSectionDay,
    notifyAncestors,
} from './tasks.util'
import { Timeline } from '../timeline/timeline'

export const LABEL_CREATE_PREFIX = 'Create '
export const SHOULD_DELETE = 'SHOULD_DELETE'

window[SHOULD_DELETE] = []

export const Tasks = ({
    view = 'list' as 'list' | 'board' | 'calendar' | string,
    workspaceId,
    projectId = null,
    teamId = null,
    type = 'project' as 'project' | 'upcoming' | 'today' | 'unassigned' | 'calendar',
    isSidebar = false,
    dontLoadTasks = false,
    fields = null,
}) => {
    const workspace = useAppStore((state) => state.workspace)
    const actions = useAppStore((state) => state.actions)
    const sections = useAppStore((state) => state.sections)
    const projects = useAppStore((state) => state.projects)
    const mainLoading = useAppStore((state) => state.mainLoading)
    const params = useParams()
    const taskLabelsAPI = useTaskLabels()
    const taskUsersAPI = useTaskUsers()
    const { showToast } = useToast()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const { setDialog, closeDialog } = useDialog()
    const tasksAPI = useTasks()
    const projectSectionsAPI = useProjectSections()
    const projectsAPI = useProjects()
    const labelsAPI = useLabels()
    const projectUsersAPI = useProjectUsers()
    const teamUsersAPI = useTeamUsers()
    const workspaceUsersAPI = useWorkspaceUsers()
    const [options, setOptions] = useState<RichInputOption[]>([])
    const [availableLabels, setAvailableLabels] = useState([])
    const [availableUsers, setAvailableUsers] = useState([])
    const { lockDrag } = useDrag()
    const navigate = useNavigate()
    const noDragging = useMemo(() => !!params.taskId, [params, type])
    const tasks = useMemo(() => sections.map((section) => flattenTasks(section)).flat(), [sections])
    const { sort, hideComplete } = useAppStore((state: any) => state.filters)
    const kanbanMode = useMemo(() => view == 'board', [view])
    const { alert } = useAlert()
    const { getStorage, setStorage } = useStorage()
    const [date, setDate] = useState(new Date())
    const [calendarView, setCalendarView] = useState(null)
    const dataCache = useRef('')
    const user = useAppStore((state) => state.user)
    const tasksHaveLoaded = useRef(false)
    const {
        showTimeline,
        showCalendar,
        showTasks,
        noProjectPrefix,
        noSectionAdd,
        noSectionEdit,
        noTaskLabels,
        noTaskDates,
        noTaskUsers,
        noSelection,
        noIndent,
        noBlankSection,
        showProjectName,
    } = useMemo(
        () => ({
            showTimeline: view == 'timeline',
            showCalendar: view == 'calendar' && calendarView != null,
            showTasks: view == 'list' || view == 'board',
            noProjectPrefix: type == 'project',
            noSectionAdd: type != 'project',
            noSectionEdit: type != 'project',
            noTaskLabels: false || view == 'timeline',
            noTaskDates: false || view == 'timeline',
            noTaskUsers: false || view == 'timeline',
            noSelection: type == 'today' || type == 'upcoming' || view == 'timeline',
            noIndent: view != 'list' ? true : type == 'upcoming' || type == 'today',
            noBlankSection: view == 'board' || type == 'project' || type == 'today' || type == 'unassigned',
            showProjectName: type == 'today' || type == 'upcoming',
        }),
        [view, type, calendarView]
    )

    // FloatingToolbar

    const handleSelectionDelete = (selection) => {
        const tasks = Object.keys(selection).map((key) => selection[key].task)
        const taskIds = flattenArray(tasks).map((t) => t.id)

        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.taskDeleteMany({ taskIds })
                            closeDialog()
                            tasksAPI.removeMany(workspaceId, projectId, taskIds)
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    // TaskMenu

    const handleTaskOpen = (task) => {
        switch (type) {
            case 'unassigned':
                return navigate(`/app/workspace/${workspaceId}/unassigned/task/${task.id}`)
            case 'calendar':
                return navigate(`/app/workspace/${workspaceId}/calendar/task/${task.id}`)
            case 'upcoming':
                return navigate(`/app/workspace/${workspaceId}/upcoming/task/${task.id}`)
            case 'today':
                return navigate(`/app/workspace/${workspaceId}/today/task/${task.id}`)
            default:
                if (params.view) {
                    return navigate(`/app/workspace/${workspaceId}/c/${projectId}/${params.view}/task/${task.id}`)
                } else {
                    return navigate(`/app/workspace/${workspaceId}/c/${projectId}/task/${task.id}`)
                }
        }
    }

    const handleTaskDelete = (task) => {
        const tasks = flattenTasks(task)

        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (tasks.length == 1) {
                                actions.taskDelete({ task })
                                tasksAPI.remove(workspaceId, projectId, task.id)
                            } else {
                                const taskIds = tasks.map((t) => t.id)
                                actions.taskDeleteMany({ taskIds })
                                tasksAPI.removeMany(workspaceId, projectId, taskIds)
                            }

                            closeDialog()
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    // TodoProvider

    const handleTaskUpdate = async (task) => {
        const { id, ...rest } = task

        actions.taskUpdate({ task })

        await tasksAPI.update(workspaceId, projectId, id, { ...rest })
    }

    const handleTaskTitleUpdate = async (taskData) => {
        const { id, index, indent, areaId } = taskData

        let parentTaskId = undefined
        const sectionIndex = getSectionIndex(areaId)
        const sectionId = sections[sectionIndex].id
        const sectionTasks = flattenArray(sections[sectionIndex].tasks)
        const shouldUpdateIndent = indent != undefined

        if (shouldUpdateIndent) {
            const parentTask = findFirstLowerIndentTask(sectionTasks, index, indent)
            parentTaskId = parentTask?.id || null
        }

        const entities = await processHTML(taskData.title)
        const title = entities.title
        const start = entities.dates?.start
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const users = removeExistingElements(entities.users, taskData.users)
        const labels = removeExistingElements(entities.labels, taskData.labels)

        // update task with details from input
        const updateTaskResult = await tasksAPI.updatev2(workspaceId, projectId, id, {
            title,
            start,
            end,
            repeat,
            wholeDay,
            users,
            labels,
            parentTask: { id: parentTaskId },
        })

        // prep the object to update the store
        const updatedTask = cleanObject({
            title,
            start,
            end,
            repeat,
            wholeDay,
            parentTaskId,
            users: [...(taskData.users || []), ...updateTaskResult.users],
            labels: [...(taskData.labels || []), ...updateTaskResult.labels],
            indent: shouldUpdateIndent ? indent : undefined,
        })

        setOptions([])

        actions.taskUpdate({ task: { id, ...updatedTask } })
    }

    const handleTaskLabelAdd = async ({ id, label }) => {
        const newLabelId = uuidv4()
        const taskLabelId = uuidv4()
        const task = findTask(sections, id)
        const taskId = task.id
        let labels = task.labels || []
        let labelId = label.id
        let newLabel = null

        // create the label if it doesn't exist
        if (!label.id) {
            newLabel = {
                id: newLabelId,
                color: label.color,
                text: label.text.replace(LABEL_CREATE_PREFIX, ''),
                description: '',
            }

            labelId = newLabel.id
            labels.push({ ...newLabel, tlid: taskLabelId })
        } else {
            labels.push({ ...label, tlid: taskLabelId })
        }

        actions.taskUpdate({ task: { id, labels } })

        if (!!newLabel) {
            actions.labelsAdd(newLabel)
            await labelsAPI.create(workspaceId, newLabel)
        }

        await taskLabelsAPI.create(workspaceId, projectId, taskId, { id: taskLabelId, labelId })
    }

    const handleTaskLabelDelete = async ({ id, label }) => {
        const task = findTask(sections, id)
        const taskLabelId = label.tlid
        const taskId = task.id
        const labelId = label.id
        let labels = task.labels || []

        labels = labels.filter((l) => l.id != label.id)

        actions.taskUpdate({ task: { id, labels } })

        await taskLabelsAPI.remove(workspaceId, projectId, taskId, taskLabelId)
    }

    const handleTaskDateUpdate = async ({ id, start, end, repeat, wholeDay }) => {
        actions.taskUpdate({ task: { id, start, end, repeat, wholeDay } })

        await tasksAPI.update(workspaceId, projectId, id, {
            start: start ? FDate(start).toISOWithTimezone() : null,
            end: end ? FDate(end).toISOWithTimezone() : null,
            repeat,
            wholeDay,
        })
    }

    const handleTaskUserAdd = async ({ id, user }) => {
        const taskUserId = uuidv4()
        const task = findTask(sections, id)
        const taskId = task.id

        const userId = user.id
        let users = task.users || []

        users.push({ ...user, tuid: taskUserId })

        actions.taskUpdate({ task: { id, users } })

        await taskUsersAPI.create(workspaceId, projectId, taskId, { id: taskUserId, userId })
    }

    const handleTaskUserDelete = async ({ id, user }) => {
        const task = findTask(sections, id)
        const taskUserId = user.tuid
        const taskId = task.id
        const userId = user.id
        let users = task.users || []

        users = users.filter((u) => u.id != user.id)

        actions.taskUpdate({ task: { id, users } })

        await taskUsersAPI.remove(workspaceId, projectId, taskId, taskUserId)
    }

    const handleTaskComplete = async ({ id, complete, start, end, repeat, wholeDay }) => {
        const updatedTask = getNewCompletedTaskDate({ complete, start, end, repeat, wholeDay })

        // update the task immediately
        actions.taskUpdate({ task: { id, ...updatedTask } })
        tasksAPI.update(workspaceId, projectId, id, { ...updatedTask })

        // for repeat tasks that have a next date (they don't get marked as checked)
        if (!updatedTask.complete && complete) {
            dispatchCustomEvent('faux-check', { id })
        } else {
            if (hideComplete && updatedTask.complete) {
                dispatchCustomEvent('faux-check', { id })

                // ready it to be removed
                window[SHOULD_DELETE] = [...window[SHOULD_DELETE], id]

                setTimeout(() => {
                    if (window[SHOULD_DELETE].includes(id)) {
                        actions.taskDelete({ task: { id, ...updatedTask } })
                    }
                }, 2000)

                showToast({
                    showProgress: true,
                    delay: 2000,
                    anchor: 'bottom-center',
                    toastComponent: ({ onDismiss }) => (
                        <View
                            row
                            width={200}
                            gap="1rem">
                            <Text
                                flex={1}
                                onClick={onDismiss}
                                colorToken="base-100">
                                Task complete.
                            </Text>
                            <View
                                p="0.5rem 1rem"
                                colorToken="base-100"
                                border="1px solid var(--f-color-surface-strongest)"
                                radius="var(--f-radius)"
                                className="f-buttonize"
                                onClick={() => {
                                    onDismiss()
                                    window[SHOULD_DELETE] = window[SHOULD_DELETE].filter((id1) => id != id1)
                                    actions.taskUpdate({ task: { id, complete: false } })
                                    tasksAPI.update(workspaceId, projectId, id, { id, complete: false })
                                    dispatchCustomEvent('faux-uncheck', { id })
                                }}>
                                Undo?
                            </View>
                        </View>
                    ),
                })
            }
        }
    }

    const handleTaskAdd = async ({ task, sectionIndex }) => {
        if (task.title == '') return

        const today = type == 'today'
        const upcoming = type == 'upcoming'
        const calendar = type == 'calendar'
        const unassigned = type == 'unassigned'
        const project = type == 'project'

        const section = sections[sectionIndex]
        const sectionId = section.id
        const sectionTitle = section.title
        const { description = '', completed = false } = task

        const sectionTasks = flattenArray(sections[sectionIndex].tasks)
        const parentTaskId = null

        const lastTask = sectionTasks.length == 0 ? null : sectionTasks[sectionTasks.length - 1]
        const lastTaskPosition = lastTask ? +lastTask.position : null

        let position = -getPositionIncrement()
        if (lastTask) position = lastTaskPosition + getPositionIncrement()

        const initialStart = today
            ? FDate(new Date()).toISOWithTimezone()
            : upcoming
            ? FDate(new Date(section.day)).toISOWithTimezone()
            : undefined

        const entities = await processHTML(task.title)
        const id = uuidv4()
        const title = entities.title
        const start = entities.dates?.start ?? initialStart
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const labels = entities.labels.map((label) => ({ tlid: uuidv4(), ...label }))
        const users = entities.users.map((user) => ({ tuid: uuidv4(), ...user }))

        const p = projects.find((project) => project.id == projectId)

        // this basically mimics the response structure from the API
        const newTask = {
            id,
            title,
            description: '',
            priority: null,
            type: null,
            position,
            complete: false,
            read: true,
            start,
            end,
            repeat,
            wholeDay,
            externalEmail: null,
            meta: {},
            projectId,
            projectSectionId: upcoming ? null : sectionId,
            projectSectionTitle: sectionTitle,
            projectName: p?.name,
            projectColor: p?.color,
            teamId,
            comments: [],
            users,
            followers: [],
            labels,
            attachments: [],
        }

        actions.taskAdd({ sectionId, task: newTask })

        // if it's a kanban column, scrolldown
        setTimeout(() => dispatchCustomEvent('todosection:scroll-down', { sectionId }))

        await tasksAPI.create(
            workspaceId,
            projectId,
            cleanObject({
                id,
                title,
                description,
                completed,
                position,
                read: true,
                projectSection: upcoming ? null : { id: sectionId }, // section ID here will be a Fold f-ueid (we need it to be null)
                parentTask: { id: parentTaskId },
                users,
                labels,
                start,
                end,
                repeat,
                wholeDay,
            })
        )
    }

    const handleMultipleTaskAdd = async ({ tasks, sectionIndex }) => {
        if (!Array.isArray(tasks) || tasks.length === 0) return

        if (tasks.length > MAX_TASK_PASTE) {
            alert({
                title: 'Sorry',
                description: `A maximum of ${MAX_TASK_PASTE} tasks are supported at the moment`,
            })
            return
        }

        const today = type === 'today'
        const upcoming = type === 'upcoming'
        const section = sections[sectionIndex]
        const sectionId = section.id
        const sectionTasks = flattenArray(section.tasks)
        const lastTaskPosition =
            sectionTasks.length > 0 ? +sectionTasks[sectionTasks.length - 1].position : -POSITION_INCREMENT
        const projectSectionId = upcoming ? null : sectionId
        const parentTaskId = null
        const newTasks = tasks
            .filter(({ title }) => !!title.trim())
            .map(({ title }, index) => ({
                id: uuidv4(),
                read: true,
                title,
                position: lastTaskPosition + getPositionIncrement() * (index + 1),
                start: today
                    ? FDate(new Date()).toISOWithTimezone()
                    : upcoming
                    ? FDate(new Date(section.day)).toISOWithTimezone()
                    : undefined,
            }))

        const tasksFromAPI = await tasksAPI.createMultiple(
            workspaceId,
            projectId,
            projectSectionId,
            parentTaskId,
            newTasks
        )

        actions.taskAddAndRebuild({
            tasks: tasksFromAPI,
            syncWithProjectId: projectId,
        })

        // if it's a kanban column, scrolldown
        setTimeout(() => dispatchCustomEvent('todosection:scroll-down', { sectionId }))
    }

    const handleTaskAddAbove = async (task) => {
        if (task.title == '') return

        const { areaId, taskId, taskIndent } = task

        const sectionIndex = getSectionIndex(areaId)
        const sectionId = sections[sectionIndex].id
        const sectionTitle = sections[sectionIndex].title
        const sectionTasks = flattenArray(sections[sectionIndex].tasks)

        //const originTask = sectionTasks[taskIndex]
        const taskIndex = sectionTasks.findIndex((task) => task.id == taskId)
        const originTask = sectionTasks[taskIndex]

        const { projectSectionId } = originTask
        const parentTaskId = originTask.parentTaskId
        const indent = taskIndent
        const position = getNewPositionForTargetIndex(sectionTasks, taskIndex)

        const entities = await processHTML(task.title)
        const id = uuidv4()
        const title = entities.title
        const start = entities.dates?.start
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const labels = entities.labels.map((label) => ({ tlid: uuidv4(), ...label }))
        const users = entities.users.map((user) => ({ tuid: uuidv4(), ...user }))

        const p = projects.find((project) => project.id == projectId)

        // this basically mimics the response structure from the API
        const newTask = {
            id,
            title,
            description: '',
            priority: null,
            type: null,
            position,
            complete: false,
            read: true,
            start,
            end,
            repeat,
            wholeDay,
            externalEmail: null,
            meta: {},
            parentTaskId,
            projectId,
            projectSectionId,
            projectSectionTitle: sectionTitle,
            projectName: p?.name,
            projectColor: p?.color,
            teamId,
            comments: [],
            users,
            followers: [],
            labels,
            attachments: [],
        }

        actions.taskAddAndRebuild({
            tasks: [newTask],
            syncWithProjectId: projectId,
        })

        // open the add below for the new task
        waitForRender(() => {
            dispatchTodoEvent('add-task-below', { id: newTask.id })
        }, 100)

        await tasksAPI.create(
            workspaceId,
            projectId,
            cleanObject({
                id,
                title,
                description: '',
                completed: false,
                read: true,
                position,
                projectSection: { id: projectSectionId },
                parentTask: { id: parentTaskId },
                project: { id: projectId },
                users,
                labels,
                start,
                end,
                repeat,
                wholeDay,
            })
        )

        notifyAncestors(sections[sectionIndex].tasks, parentTaskId)
    }

    const handleTaskAddBelow = async (task) => {
        if (task.title == '') return

        const { areaId, taskId, taskIndent, shouldIndent } = task

        const sectionIndex = getSectionIndex(areaId)
        const sectionId = sections[sectionIndex].id
        const sectionTitle = sections[sectionIndex].title
        const sectionTasks = flattenArray(sections[sectionIndex].tasks)

        //const originTask = sectionTasks[taskIndex]
        const taskIndex = sectionTasks.findIndex((task) => task.id == taskId)
        const originTask = sectionTasks[taskIndex]

        const { projectSectionId } = originTask
        const parentTaskId = shouldIndent ? taskId : originTask.parentTaskId
        const indent = shouldIndent ? taskIndent + 1 : taskIndent
        const position = getNewPositionForTargetIndex(sectionTasks, taskIndex + 1)

        const entities = await processHTML(task.title)
        const id = uuidv4()
        const title = entities.title
        const start = entities.dates?.start
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const labels = entities.labels.map((label) => ({ tlid: uuidv4(), ...label }))
        const users = entities.users.map((user) => ({ tuid: uuidv4(), ...user }))

        const p = projects.find((project) => project.id == projectId)

        // this basically mimics the response structure from the API
        const newTask = {
            id,
            title,
            description: '',
            priority: null,
            type: null,
            position,
            complete: false,
            read: true,
            start,
            end,
            repeat,
            wholeDay,
            externalEmail: null,
            meta: {},
            parentTaskId,
            projectId,
            projectSectionId,
            projectSectionTitle: sectionTitle,
            projectName: p?.name,
            projectColor: p?.color,
            teamId,
            comments: [],
            users,
            followers: [],
            labels,
            attachments: [],
        }

        actions.taskAddAndRebuild({
            tasks: [newTask],
            syncWithProjectId: projectId,
        })

        // open the add below for the new task
        waitForRender(() => {
            dispatchTodoEvent('add-task-below', { id: newTask.id })
        }, 100)

        await tasksAPI.create(
            workspaceId,
            projectId,
            cleanObject({
                id,
                title,
                description: '',
                completed: false,
                read: true,
                position,
                projectSection: { id: projectSectionId },
                parentTask: { id: parentTaskId },
                project: { id: projectId },
                users,
                labels,
                start,
                end,
                repeat,
                wholeDay,
            })
        )

        notifyAncestors(sections[sectionIndex].tasks, parentTaskId)
    }

    const handleTaskMove = async ({ origin, target, selection }) => {
        try {
            if (noDragging) return

            const isUpcoming = type == 'upcoming'
            const isSelection = Object.keys(selection).length > 0

            const targetIndent = kanbanMode ? 0 : isNaN(+target.indent) ? 0 : +target.indent
            const targetMoveDirection = target.moveDirection
            const targetElementId = target.elementId
            const targetTasks = isSelection
                ? Object.keys(selection)
                      .map((key) => selection[key].task)
                      .sort(byPosition)
                : [window[FOLD_TODO_DRAG_TASK_CACHE]]
            const targetSectionIndex = getSectionIndex(target.areaId)
            const targetSectionId = sections[targetSectionIndex].id
            const targetTasksFlattened = flattenArray(targetTasks, targetIndent)

            // get section where to insert tasks
            const sectionId = sections[targetSectionIndex].id
            const sectionTasks = flattenArray(sections[targetSectionIndex].tasks)

            const movementAdjuster = targetMoveDirection == 'down' ? 1 : 0
            const targetElementIndex = targetElementId ? sectionTasks.findIndex((t) => t.id == targetElementId) : 0
            let targetIndex = targetElementIndex + movementAdjuster

            // if the previous task is collapsed (only possible if it's a parent)
            // then add the child count to the target index so it skips the collapsed children
            if (getCollapsedStateFromLocalStorage(TASK_COLLAPSED, targetElementId) && targetMoveDirection == 'down') {
                targetIndex += findSubtaskCount(sections[targetSectionIndex].tasks, targetElementId)
            }

            // also get the parent for the new position
            const parentTask = findFirstLowerIndentTask(sectionTasks, targetIndex, targetIndent)
            const parentTaskId = parentTask?.id || null
            const positions = getNewPositionsForTasks(
                sectionTasks,
                targetIndex,
                targetIndent,
                targetTasksFlattened.length
            )

            logger(parentTask)
            logger(positions)

            // save these on the object
            const tasks = targetTasksFlattened.map((task, index) => {
                return {
                    id: task.id,
                    position: +positions[index],
                    start: isUpcoming ? getUpcomingSectionDay(sectionId, sections) : task.start,
                    end: isUpcoming ? null : task.end,
                    parentTaskId: task.indent == 0 ? null : task.root ? parentTaskId : task.parentTaskId,
                    projectSectionId: isUpcoming ? undefined : targetSectionId,
                }
            })

            actions.taskMove({ tasks })

            tasksAPI.updateManyFromMove(
                workspaceId,
                projectId,
                tasks.map(({ id, position, parentTaskId, start, end }) => ({
                    id,
                    position,
                    parentTask: { id: parentTaskId },
                    projectSection: isUpcoming ? undefined : { id: targetSectionId },
                    start,
                    end,
                }))
            )

            window[FOLD_TODO_DRAG_TASK_CACHE] = null

            // this finds the ids of the selection or tasks parent where it was dragged from
            // we use these to notifify their tasks to reload the subtasks
            const originParentTaskIds = isSelection
                ? Object.keys(selection).map((key) => document.getElementById(selection[key].task.id)?.dataset.parent)
                : [document.getElementById(origin.elementId)?.dataset.parent]
            const uniqueArray = [...new Set([parentTaskId, ...originParentTaskIds])].filter(Boolean)
            notifyAncestors(sections[targetSectionIndex].tasks, uniqueArray)
        } catch (e) {
            logger(e)
        }
    }

    const handleTaskMoveToProject = async ({ origin, target, selection }) => {
        if (!window[FOLD_TODO_DRAG_TASK_CACHE]) return

        const projectId = target.elementId
        const draggedTask = window[FOLD_TODO_DRAG_TASK_CACHE]
        const taskId = draggedTask.id
        const selectedTasks = Object.keys(selection).map((key) => selection[key].task)
        const hasSelection = selectedTasks.length > 0
        const tasksToInsert = hasSelection
            ? selectedTasks.map((task) => ({ ...task, indent: 0, parentTaskId: null }))
            : [{ ...draggedTask, indent: 0, parentTaskId: null }]
        const tasksToInsertFlattened = flattenArray(tasksToInsert, 0)
        const tasksToInsertIds = extractIds(tasksToInsertFlattened)

        if (type == 'upcoming' || type == 'today' || type == 'calendar') {
            const taskId = tasksToInsertIds[0]
            const project = projects.find((project) => project.id == projectId)
            const tasks = await tasksAPI.getSubtasks(workspaceId, projectId, taskId)
            const taskIds = [taskId, ...(tasks || []).map((t) => t.id)]

            actions.taskUpdateProject({
                taskIds,
                task: {
                    projectName: project.name,
                    projectColor: project.color,
                },
            })

            tasksAPI.moveToProject(
                workspaceId,
                projectId,
                taskIds.map((taskId) => ({ id: taskId }))
            )
        } else {
            const originProjectId = draggedTask.projectId
            const targetProjectId = target.elementId

            if (originProjectId == targetProjectId) return

            actions.taskDeleteMany({ 
                taskIds: tasksToInsertIds,
            })

            actions.taskAddAndRebuild({
                tasks: tasksToInsert,
                syncWithProjectId: targetProjectId,
                skipHost: true,
            })

            tasksAPI.moveToProject(
                workspaceId,
                projectId,
                tasksToInsert.map(({ id }) => ({ id }))
            )
        }

        window[FOLD_TODO_DRAG_TASK_CACHE] = null
    }

    const handleSectionDelete = (section) => {
        const sectionId = section.id

        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone & all tasks will be deleted.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            showToast({
                                message: 'Section deleted.',
                                anchor: 'bottom-center',
                                prefix: <IconLib icon="check" />,
                            })
                            actions.sectionDelete(sectionId)
                            closeDialog()
                            projectSectionsAPI.remove(workspaceId, projectId, section.id)
                        }}>
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleSectionUpdate = async (section) => {
        const { id, ...rest } = section
        const projectSectionId = section.id

        actions.sectionUpdate(section)

        await projectSectionsAPI.update(workspaceId, projectId, projectSectionId, { ...rest })
    }

    const handleSectionAdd = async ({ title, sectionIndex }) => {
        const targetIndex = sectionIndex + 1
        const position = getNewPositionForTargetIndex(sections, targetIndex)
        const id = uuidv4()

        actions.sectionAdd({
            section: { id, title, position },
            targetIndex,
        })

        await projectSectionsAPI.create(workspaceId, projectId, {
            id,
            title,
            description: '',
            position,
            color: undefined,
        })
    }

    const handleSectionMove = async ({ origin, target }) => {
        const originIndex = origin.index
        const targetIndex = target.index
        const projectSectionId = sections[origin.index].id

        if (originIndex != targetIndex) {
            const position = getNewPositionForTargetIndex(sections, targetIndex)
            actions.sectionMove({ projectSectionId, position })
            await projectSectionsAPI.update(workspaceId, projectId, projectSectionId, { position })
        }
    }

    // RichInputProvider

    const handleTrigger = async (text) => {
        const word = text.trim()

        if (word.charAt(0) == '@') {
            const username = word.substring(1) || ''
            const users = teamId
                ? await teamUsersAPI.getSearch(workspaceId, teamId, username)
                : projectId
                ? await projectUsersAPI.getSearch(workspaceId, projectId, username)
                : await workspaceUsersAPI.getSearch(workspaceId, username)

            setOptions(
                users
                    .map(({ id, image, username }) => ({
                        id,
                        color: undefined,
                        name: '@' + username,
                        image,
                        type: 'user',
                        value: username,
                    }))
                    .sort(byName)
            )
        } else if (word.charAt(0) == '#') {
            const label = word.substring(1) || ''
            const labels = await labelsAPI.getSearch(workspaceId, label)
            setOptions(
                labels
                    .map(({ id, text, color }) => ({
                        id,
                        color,
                        name: '#' + text,
                        type: 'label',
                        value: text,
                    }))
                    .sort(byName)
            )
        } else {
            setOptions([])
        }
    }

    // CommonProvider

    const handleUserFilter = async (query, context = null) => {
        const finalProjectId = context.projectId || projectId
        const finalTeamId = context.teamId || teamId

        setError(null)

        try {
            const users = finalTeamId
                ? await teamUsersAPI.getSearch(workspaceId, finalTeamId, query)
                : finalProjectId
                ? await projectUsersAPI.getSearch(workspaceId, finalProjectId, query)
                : await workspaceUsersAPI.getSearch(workspaceId, query)

            const present = users.find((u) => u.id == user.id)
            const availableUsers = present ? users.sort(byName) : [...users, user].sort(byName)

            setAvailableUsers(availableUsers)
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleLabelFilter = async (query = '') => {
        setError(null)

        try {
            //const labels = await labelsAPI.getSearch(workspaceId, query)
            const labels = searchLabels(query)
            const newLabel = { id: null, text: LABEL_CREATE_PREFIX + query, color: getRandomColor() }
            const noDirectMatch = !labels.find(({ text }) => text.toLowerCase() == query.toLowerCase()) && !!query

            if (noDirectMatch) {
                setAvailableLabels([newLabel, ...labels.sort(byText)])
            } else {
                setAvailableLabels(labels.sort(byText))
            }
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    // Calendar

    const handleForward = () => {
        switch (calendarView) {
            case 0:
                setDate(FDate(date).add(1, 'day'))
                break
            case 1:
                setDate(FDate(date).add(+workspace.weekLength, 'day'))
                break
            case 2:
                setDate(FDate(date).add(1, 'month'))
                break
        }
    }

    const handleBackward = () => {
        switch (calendarView) {
            case 0:
                setDate(FDate(date).subtract(1, 'day'))
                break
            case 1:
                setDate(FDate(date).subtract(+workspace.weekLength, 'day'))
                break
            case 2:
                setDate(FDate(date).subtract(1, 'month'))
                break
        }
    }

    const handleToday = () => {
        setDate(new Date())
    }

    const handleViewChange = (index) => {
        const cacheId = type == 'calendar' ? 'calendar' : projectId
        setCalendarView(index)
        const calendarStorage = JSON.parse(getStorage('FRONTEER_CALENDAR') || '{}')
        calendarStorage[cacheId] = index
        setStorage('FRONTEER_CALENDAR', JSON.stringify(calendarStorage))
    }

    // -------------

    const fetchSectionsAndTasks = async () => {
        try {
            const filters = getSavedFilters(projectId)
            const { hideComplete } = filters

            // until we can fix sorting again
            const sort = null
            const dates = getDates(type, view, calendarView, date)

            lockDrag(!!sort)

            if (type == 'unassigned') {
                return {
                    tasks: await tasksAPI.getUnassigned(workspaceId),
                    sections: [
                        {
                            id: null,
                            title: 'Unassigned',
                            color: undefined,
                            desription: '',
                            position: 0,
                            editableName: false,
                            taskCount: true,
                        },
                    ],
                }
            }

            if (type == 'today') {
                return {
                    tasks: await tasksAPI.getToday(workspaceId, dates.start, dates.end),
                    sections: [
                        {
                            id: null,
                            title: 'Today',
                            color: undefined,
                            description: '',
                            position: 0,
                            editableName: false,
                            taskCount: true,
                            day: new Date(),
                        },
                    ],
                }
            }

            if (type == 'upcoming') {
                const { start, end } = createDatesForUpcoming()
                const days = getDaysBetween(start, end)

                return {
                    tasks: await tasksAPI.getUpcoming(workspaceId, dates.start, dates.end),
                    sections: days.map((day) => ({
                        id: generateUEID(),
                        title: new Intl.DateTimeFormat('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                        }).format(day),
                        color: undefined,
                        description: '',
                        position: 0,
                        editableName: false,
                        taskCount: true,
                        day,
                    })),
                }
            }

            if (type == 'calendar') {
                return {
                    tasks: await tasksAPI.getCalendar(workspaceId),
                    sections: [
                        {
                            id: null,
                            title: 'Calendar',
                            color: undefined,
                            desription: '',
                            position: 0,
                            editableName: false,
                            taskCount: true,
                        },
                    ],
                }
            }

            if (type == 'project' && projectId) {
                const { tasks, sections } = await tasksAPI.getAll(workspaceId, projectId, {
                    sort,
                    hideComplete,
                    hideSubtasks: undefined,
                })
                const position = sections[0] ? +sections[0].position - POSITION_INCREMENT : 0

                return {
                    tasks,
                    sections: [
                        {
                            id: null,
                            title: 'No Section',
                            color: undefined,
                            description: '',
                            position,
                            editableName: false,
                            taskCount: true,
                        },
                        ...sections,
                    ],
                }
            }

            return {
                tasks: [],
                sections: [],
            }
        } catch (e) {
            setError('A problem occured.')
        }
    }

    const fetchData = async (dontLoadTasks) => {
        // if the tasks have already loaded, then don't load them again
        // so just do the initial populating - chances are they have already been laoded
        // unless it's a channel
        if (dontLoadTasks) return
        if (isSidebar && tasksHaveLoaded.current) return

        actions.setMainLoading(true)
        setError(null)

        try {
            const value = await fetchSectionsAndTasks()

            if (!value) {
                actions.setMainLoading(false)
                console.error('tasks/sections from api are null', value)
                return
            }

            const { tasks, sections } = value

            actions.buildSectionHierarchy(sections, tasks)
            actions.setMainLoading(false)
        } catch (e) {
            logger(e)
            actions.setMainLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useCustomEvent('reload', ({ detail }) => {
        fetchData(false)
    })

    useEffect(() => {
        const cacheId = type == 'calendar' ? 'calendar' : projectId
        const calendarView = view == 'calendar' ? getInitialCalendarView(cacheId, isSidebar) : null

        if (cacheId) setCalendarView(calendarView)
    }, [projectId, type, view, isSidebar])

    useEffect(() => {
        const key = workspaceId + projectId + sort + hideComplete

        // this ensures data gets fetched once
        if (workspaceId && dataCache.current != key) {
            dataCache.current = key

            // dontLoadTasks is true for sidebar/no-channel
            // this keeps the tasks only loading once with multiple views
            // we force it though on explicit 'reload' events
            fetchData(dontLoadTasks)
        }
    }, [workspaceId, projectId, sort, hideComplete, dontLoadTasks])

    return (
        <MenuProvider
            menu={({ data: { target, payload }, dismiss }) => {
                if (!payload) return null

                const taskId = payload.id
                const taskFromSections = findTaskById([...sections], taskId)

                return (
                    <TaskMenu
                        showAddAbove={view == 'list'}
                        showAddBelow={view == 'list'}
                        type={type}
                        task={taskFromSections}
                        onCancel={dismiss}
                        onTodoAddAbove={() => {
                            dismiss()
                            dispatchTodoEvent('add-task-above', { id: taskId })
                        }}
                        onTodoAddBelow={() => {
                            dismiss()
                            dispatchTodoEvent('add-task-below', { id: taskId })
                        }}
                        onSave={(task) => {
                            dismiss()
                            handleTaskUpdate({ id: taskId, ...task })
                        }}
                        onView={() => {
                            dismiss()
                            handleTaskOpen({ ...taskFromSections })
                        }}
                        onDelete={() => {
                            handleTaskDelete(taskFromSections)
                        }}
                    />
                )
            }}>
            <LoadingLine
                loading={mainLoading}
                variant="highlight"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 100,
                }}
            />

            <RichInputProvider
                triggers={['#', '@']}
                options={options}
                onTrigger={handleTrigger}>
                <CommonProvider
                    onUserFilter={handleUserFilter}
                    onLabelFilter={handleLabelFilter}
                    availableLabels={availableLabels}
                    availableUsers={availableUsers}
                    colors={COLORS}>
                    {showTasks && (
                        <TodoProvider
                            id="todo-instance"
                            onTaskOpen={handleTaskOpen}
                            onTaskUpdate={handleTaskUpdate}
                            onTaskTitleUpdate={handleTaskTitleUpdate}
                            onTaskLabelAdd={handleTaskLabelAdd}
                            onTaskLabelDelete={handleTaskLabelDelete}
                            onTaskDateUpdate={handleTaskDateUpdate}
                            onTaskUserAdd={handleTaskUserAdd}
                            onTaskUserDelete={handleTaskUserDelete}
                            onTaskComplete={handleTaskComplete}
                            onTaskAddBelow={handleTaskAddBelow}
                            onTaskAddAbove={handleTaskAddAbove}
                            onTaskAdd={handleTaskAdd}
                            onMultipleTaskAdd={handleMultipleTaskAdd}
                            onTaskMove={handleTaskMove}
                            onTaskMoveToProject={handleTaskMoveToProject}
                            onSectionDelete={handleSectionDelete}
                            onSectionUpdate={handleSectionUpdate}
                            onSectionAdd={handleSectionAdd}
                            onSectionMove={handleSectionMove}
                            noProjectPrefix={noProjectPrefix}
                            noSectionAdd={noSectionAdd}
                            noSectionEdit={noSectionEdit}
                            noTaskLabels={noTaskLabels}
                            noTaskUsers={noTaskUsers}
                            noTaskDates={noTaskDates}
                            noSelection={noSelection}
                            noIndent={noIndent}
                            noBlankSection={noBlankSection}
                            noDragging={noDragging}
                            kanbanMode={kanbanMode}
                            showProjectName={showProjectName}
                            fields={fields}>
                            <Todo
                                sections={sections}
                                toolbar={({ selection }) => (
                                    <FloatingToolbar
                                        selection={selection}
                                        onCancel={() => {
                                            dispatchTodoEvent('select', { instanceId: 'todo-instance' })
                                        }}
                                        onDelete={() => {
                                            handleSelectionDelete({ ...selection })
                                            dispatchTodoEvent('select', { instanceId: 'todo-instance' })
                                        }}
                                        onUserUpdate={() => null}
                                        onUserFilter={handleUserFilter}
                                        onLabelUpdate={() => null}
                                        onLabelFilter={handleLabelFilter}
                                    />
                                )}
                            />
                        </TodoProvider>
                    )}

                    {showCalendar && (
                        <Calendar
                            isSidebar={isSidebar}
                            type={type}
                            tasks={tasks}
                            teamId={teamId}
                            view={calendarView}
                            date={date}
                            onBack={handleBackward}
                            onForward={handleForward}
                            onToday={handleToday}
                            onViewChange={handleViewChange}
                            onTaskUpdate={handleTaskUpdate}
                            onTaskUserAdd={handleTaskUserAdd}
                            onTaskUserDelete={handleTaskUserDelete}
                        />
                    )}

                    {showTimeline && (
                        <Timeline
                            sections={sections}
                            tasks={tasks}
                            teamId={teamId}
                            date={date}
                            onTaskOpen={handleTaskOpen}
                            onTaskUpdate={handleTaskUpdate}
                        />
                    )}
                </CommonProvider>
            </RichInputProvider>
        </MenuProvider>
    )
}
